import { useEffect, useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { searchLocations, type LocationResult } from '../../../services/locationService';

function useDebouncedValue<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

interface Props {
  label?: string;
  value: string;
  onAddressChange: (address: string) => void;
  onSelect: (loc: LocationResult) => void;
  country?: string;
}

export default function AddressLookupField({
  label = 'Street Address',
  value,
  onAddressChange,
  onSelect,
  country,
}: Props) {
  const [input, setInput] = useState(value);
  const debounced = useDebouncedValue(input, 350);

  useEffect(() => {
    setInput(value);
  }, [value]);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['user-address-search', debounced, country],
    queryFn: () => searchLocations(debounced, { limit: 8 }),
    enabled: debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  return (
    <Autocomplete
      freeSolo
      fullWidth
      size="small"
      options={options}
      loading={isFetching}
      filterOptions={(x) => x}
      inputValue={input}
      onInputChange={(_, v, reason) => {
        setInput(v);
        if (reason !== 'reset') onAddressChange(v);
      }}
      onChange={(_, opt) => {
        if (!opt || typeof opt === 'string') return;
        setInput(opt.address);
        onAddressChange(opt.address);
        onSelect(opt);
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.address || o.label)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText={debounced.trim().length < 2 ? 'Type at least 2 characters' : 'No matching addresses'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Start typing to search addresses"
          helperText="Select a suggestion to auto-fill city, state, PIN, and country"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body2" fontWeight={700}>{option.label}</Typography>
            <Typography variant="caption" color="text.secondary">{option.address}</Typography>
          </Box>
        </Box>
      )}
    />
  );
}
